import { useState, useEffect } from 'react';
import { apiCall } from '../../config/api';
import Toast from '../../components/Toast';
import { useToast } from '../../hooks/useToast';

interface HealthFund {
  id: string;
  name: string;
  code: string;
  fund_identification_code: string;
}

interface Schedule {
  id: string;
  health_fund: string;
  health_fund_name: string;
  version_code: string;
  effective_date: string;
  end_date: string | null;
  state_code: string | null;
  item_count: number;
}

interface ScheduleItem {
  id: string;
  mbs_item_number: string;
  schedule_fee: string | null;
  benefit_amount: string | null;
  benefit_percentage: string | null;
  gap_cover_amount: string | null;
  notes: string;
}

export const FeeScheduleViewer: React.FC = () => {
  const [healthFunds, setHealthFunds] = useState<HealthFund[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [scheduleItems, setScheduleItems] = useState<ScheduleItem[]>([]);
  const [selectedFund, setSelectedFund] = useState<string>('');
  const [selectedSchedule, setSelectedSchedule] = useState<string>('');
  const [serviceDate, setServiceDate] = useState<string>('');
  const [itemNumberSearch, setItemNumberSearch] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [itemsLoading, setItemsLoading] = useState(false);
  
  const { toast, showToast, hideToast } = useToast();

  useEffect(() => {
    fetchHealthFunds();
  }, []);

  useEffect(() => {
    if (selectedFund) {
      fetchSchedules();
    } else {
      setSchedules([]);
      setSelectedSchedule('');
    }
  }, [selectedFund, serviceDate]);

  useEffect(() => {
    if (selectedSchedule) {
      fetchScheduleItems();
    } else {
      setScheduleItems([]);
    }
  }, [selectedSchedule, itemNumberSearch]);

  const fetchHealthFunds = async () => {
    try {
      const response = await apiCall('/billing/health-funds/?is_active=true');
      if (response.ok) {
        const data = await response.json();
        setHealthFunds(data.results || data || []);
      }
    } catch (error) {
      showToast('Failed to load health funds', 'error');
    }
  };

  const fetchSchedules = async () => {
    setLoading(true);
    try {
      let url = `/billing/health-fund-schedules/?health_fund=${selectedFund}`;
      if (serviceDate) {
        url += `&effective_date=${serviceDate}`;
      }
      const response = await apiCall(url);
      if (response.ok) {
        const data = await response.json();
        const schedules = data.results || data || [];
        setSchedules(schedules);
        
        // Auto-select most recent schedule if service date provided
        if (schedules.length > 0 && serviceDate) {
          setSelectedSchedule(schedules[0].id);
        }
      }
    } catch (error) {
      showToast('Failed to load schedules', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchScheduleItems = async () => {
    setItemsLoading(true);
    try {
      const response = await apiCall(`/billing/health-fund-schedules/${selectedSchedule}/items/`);
      if (response.ok) {
        const data = await response.json();
        let items = data.results || data || [];
        
        // Filter by item number if search is provided
        if (itemNumberSearch) {
          items = items.filter((item: ScheduleItem) =>
            item.mbs_item_number.includes(itemNumberSearch)
          );
        }
        
        setScheduleItems(items);
      }
    } catch (error) {
      showToast('Failed to load schedule items', 'error');
    } finally {
      setItemsLoading(false);
    }
  };

  const calculateBenefit = (item: ScheduleItem): string => {
    if (item.benefit_amount) {
      return `$${parseFloat(item.benefit_amount).toFixed(2)}`;
    }
    if (item.benefit_percentage && item.schedule_fee) {
      const benefit = (parseFloat(item.schedule_fee) * parseFloat(item.benefit_percentage)) / 100;
      return `$${benefit.toFixed(2)} (${item.benefit_percentage}%)`;
    }
    if (item.benefit_percentage) {
      return `${item.benefit_percentage}% of MBS fee`;
    }
    return 'N/A';
  };

  const selectedScheduleData = schedules.find(s => s.id === selectedSchedule);

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">Fee Schedules</h1>

      {/* Search Filters */}
      <div className="card bg-base-100 shadow-xl mb-6">
        <div className="card-body">
          <h2 className="card-title mb-4">Search Criteria</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Health Fund Selection */}
            <div className="form-control">
              <label className="label">
                <span className="label-text font-semibold">Health Fund</span>
              </label>
              <select
                className="select select-bordered w-full"
                value={selectedFund}
                onChange={(e) => {
                  setSelectedFund(e.target.value);
                  setSelectedSchedule('');
                }}
              >
                <option value="">All Funds</option>
                {healthFunds.map((fund) => (
                  <option key={fund.id} value={fund.id}>
                    {fund.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Service Date */}
            <div className="form-control">
              <label className="label">
                <span className="label-text font-semibold">Service Date</span>
              </label>
              <input
                type="date"
                className="input input-bordered w-full"
                value={serviceDate}
                onChange={(e) => setServiceDate(e.target.value)}
                placeholder="Select service date"
              />
              <label className="label">
                <span className="label-text-alt">Finds schedules effective on this date</span>
              </label>
            </div>

            {/* Schedule Version */}
            <div className="form-control">
              <label className="label">
                <span className="label-text font-semibold">Schedule Version</span>
              </label>
              <select
                className="select select-bordered w-full"
                value={selectedSchedule}
                onChange={(e) => setSelectedSchedule(e.target.value)}
                disabled={!selectedFund || loading}
              >
                <option value="">Select Schedule</option>
                {schedules.map((schedule) => (
                  <option key={schedule.id} value={schedule.id}>
                    {schedule.version_code} - {schedule.effective_date}
                    {schedule.state_code ? ` (${schedule.state_code})` : ''}
                  </option>
                ))}
              </select>
              <label className="label">
                <span className="label-text-alt">
                  {schedules.length} schedule{schedules.length !== 1 ? 's' : ''} available
                </span>
              </label>
            </div>

            {/* Item Number Search */}
            <div className="form-control">
              <label className="label">
                <span className="label-text font-semibold">Item Number/Code</span>
              </label>
              <input
                type="text"
                className="input input-bordered w-full"
                placeholder="e.g., 104"
                value={itemNumberSearch}
                onChange={(e) => setItemNumberSearch(e.target.value)}
                disabled={!selectedSchedule}
              />
              <label className="label">
                <span className="label-text-alt">Search by MBS item number</span>
              </label>
            </div>
          </div>

          {/* Schedule Info */}
          {selectedScheduleData && (
            <div className="alert alert-info mt-4">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="stroke-current shrink-0 w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <div>
                <div className="font-bold">{selectedScheduleData.health_fund_name}</div>
                <div className="text-sm">
                  Version: {selectedScheduleData.version_code} | 
                  Effective: {selectedScheduleData.effective_date}
                  {selectedScheduleData.end_date && ` | Ends: ${selectedScheduleData.end_date}`}
                  {selectedScheduleData.state_code && ` | State: ${selectedScheduleData.state_code}`}
                  {' | '}
                  Items: {selectedScheduleData.item_count}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Results Table */}
      <div className="card bg-base-100 shadow-xl">
        <div className="card-body">
          <h2 className="card-title mb-4">
            Schedule Items
            {scheduleItems.length > 0 && (
              <span className="badge badge-primary">{scheduleItems.length} items</span>
            )}
          </h2>

          {itemsLoading ? (
            <div className="flex justify-center py-12">
              <span className="loading loading-spinner loading-lg"></span>
            </div>
          ) : scheduleItems.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="table table-zebra w-full">
                <thead>
                  <tr>
                    <th>Item Number</th>
                    <th>Schedule Fee</th>
                    <th>Benefit</th>
                    <th>Gap Cover</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {scheduleItems.map((item) => (
                    <tr key={item.id}>
                      <td className="font-mono font-bold">{item.mbs_item_number}</td>
                      <td>
                        {item.schedule_fee
                          ? `$${parseFloat(item.schedule_fee).toFixed(2)}`
                          : 'MBS/Derived'}
                      </td>
                      <td>{calculateBenefit(item)}</td>
                      <td>
                        {item.gap_cover_amount
                          ? `$${parseFloat(item.gap_cover_amount).toFixed(2)}`
                          : '-'}
                      </td>
                      <td className="text-sm">{item.notes || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-16 w-16 mx-auto text-base-300 mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <p className="text-lg text-base-content/60">
                {!selectedFund
                  ? 'Select a health fund to view fee schedules'
                  : !selectedSchedule
                  ? 'Select a schedule version to view items'
                  : 'No items found matching your search'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Help Information */}
      <div className="alert mt-6">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="stroke-info shrink-0 w-6 h-6">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        <div>
          <h3 className="font-bold">Billing Date vs Service Date</h3>
          <p className="text-sm">
            When billing for services, always use the schedule effective on the <strong>date of service</strong>, 
            not the billing date. For example, a procedure on 10 Nov 2025 should use the schedule effective 
            on that date, even if billed later (e.g., 25 Nov 2025).
          </p>
        </div>
      </div>

      {/* Toast Notifications */}
      <Toast message={toast.message} type={toast.type} visible={toast.visible} onClose={hideToast} />
    </div>
  );
};
